import React, { useRef, useCallback } from 'react';
import { MapPin, Search } from 'lucide-react';

/**
 * Toggle component for tablet view - switches between Scene and Clues panels
 * On tablet, LeftPanel (Character) is always visible, this toggles the right section
 */

const toggleOptions = [
  { id: 'scene', label: 'Scene', icon: MapPin },
  { id: 'clues', label: 'Clues', icon: Search }
];

export function TabletPanelToggle({
  activePanel,
  setActivePanel,
  className = '',
  ariaLabel = 'Panel navigation'
}) {
  const tabRefs = useRef({});

  const handleKeyDown = useCallback((e, currentIndex) => {
    const optionIds = toggleOptions.map(o => o.id);
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = currentIndex === 0 ? optionIds.length - 1 : currentIndex - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        newIndex = currentIndex === optionIds.length - 1 ? 0 : currentIndex + 1;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = optionIds.length - 1;
        break;
      default:
        return;
    }

    setActivePanel(optionIds[newIndex]);
    tabRefs.current[optionIds[newIndex]]?.focus();
  }, [setActivePanel]);

  return (
    <div
      className={`flex bg-black/40 border-b border-white/10 ${className}`}
      role="tablist"
      aria-label={ariaLabel}
    >
      {toggleOptions.map((option, index) => {
        const Icon = option.icon;
        const isActive = activePanel === option.id;

        return (
          <button
            key={option.id}
            ref={el => tabRefs.current[option.id] = el}
            id={`tab-${option.id}`}
            onClick={() => setActivePanel(option.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`
              flex-1 flex items-center justify-center gap-2
              min-h-touch py-2.5 px-4
              text-sm font-medium
              transition-colors duration-200
              ${isActive
                ? 'text-white bg-white/10 border-b-2 border-amber-500'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
              }
            `}
            aria-selected={isActive}
            aria-controls={`panel-${option.id}`}
            tabIndex={isActive ? 0 : -1}
            role="tab"
          >
            <Icon className="w-4 h-4" />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default TabletPanelToggle;
